import { makeAutoObservable, observable, runInAction } from "mobx";
import { teamFamilyIds } from "@/helpers/team-tree.helper";
import type { IntakeService } from "@/services/intake.service";
import type { SlaService } from "@/services/sla.service";
import type { WorkspaceTeamService } from "@/services/workspace-team.service";
import type { ISlaWorkflowCondition, ITeamSla, TTeamSlaRelation } from "@/types/sla";

/** The three groups the team settings SLA tab renders, most to least actionable
 *  (NG-15 §5.4.4). The server already orders the page this way; the grouping
 *  here is what lets each group carry its own heading and explanation. */
export const TEAM_SLA_RELATIONS: TTeamSlaRelation[] = ["manages", "inherited", "residual"];

export type TTeamSlaGroups = Record<TTeamSlaRelation, ITeamSla[]>;

/**
 * What this store needs, named one method at a time rather than taken from
 * `RootStore`.
 *
 * Importing `RootStore` for its type is enough to close a
 * `root.store -> team-sla.store -> root.store` cycle, and the repo's
 * dependency-cruiser gate treats cycles as a ratchet: waiving a new one by
 * growing the committed baseline is how the gate stops meaning anything. The
 * store genuinely needs three service calls, so it asks for those.
 */
export interface ITeamSlaDeps {
  slaService: Pick<SlaService, "listAllTeamSlas">;
  intakeService: Pick<IntakeService, "listForms">;
  workspaceTeamService: Pick<WorkspaceTeamService, "list">;
}

/**
 * The SLAs relevant to one team (NG-15 §3.3) plus the workflow options a lead
 * may scope a new SLA to.
 *
 * Deliberately separate from {@link import("./sla.store").SlaStore}: that store
 * is the workspace-wide list bank, and folding a team's narrowed, relation-
 * labelled rows into it would make "which list am I looking at" ambiguous for
 * every reader of `sla.slas`.
 *
 * Both caches are keyed to a team AND fenced by a load token. Team settings
 * keeps this store mounted across a team switch, so without both an in-flight
 * request for the team the user just left can land afterwards and repaint its
 * rows under the new team's URL.
 */
export class TeamSlaStore {
  rows: ITeamSla[] = [];
  /** Active workflows this team's family MANAGES — the option set for a
   *  lead-created SLA. Client-side narrowing is UX only; the server enforces it
   *  and answers `sla_unmanaged_workflow` for anything outside the set. */
  workflows: ISlaWorkflowCondition[] = [];
  isLoading = false;
  isLoadingWorkflows = false;
  loadError: Error | null = null;

  /** Which team `rows` belong to — `null` before the first successful load and
   *  whenever a load is in flight for a different team. */
  loadedTeamId: string | null = null;
  private loadedWorkflowsTeamId: string | null = null;
  private rowsToken = 0;
  private workflowsToken = 0;

  private deps: ITeamSlaDeps;

  constructor(deps: ITeamSlaDeps) {
    this.deps = deps;
    makeAutoObservable(
      this,
      {
        deps: false,
        rows: observable.ref,
        workflows: observable.ref,
        loadedWorkflowsTeamId: false,
        rowsToken: false,
        workflowsToken: false,
      } as never,
      { autoBind: true },
    );
  }

  /** Load (or reload) every SLA relevant to *teamId*, replacing what is cached.
   *
   *  Records failure in `loadError` and never rejects, so callers can `void` it
   *  without a catch that would have to re-log what this already logged. */
  async loadRows(slug: string, teamId: string): Promise<void> {
    const token = ++this.rowsToken;
    const isLatest = () => this.rowsToken === token;
    try {
      // Drop another team's rows up front, so the list falls back to its
      // skeleton instead of rendering the previous team's SLAs under this
      // team's URL.
      if (this.loadedTeamId !== teamId) {
        this.rows = [];
        this.loadedTeamId = null;
      }
      this.isLoading = true;
      this.loadError = null;
      const rows = await this.deps.slaService.listAllTeamSlas(slug, teamId);
      runInAction(() => {
        if (!isLatest()) return; // a newer load owns the cache
        this.rows = rows;
        this.loadedTeamId = teamId;
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      runInAction(() => {
        if (!isLatest()) return;
        this.loadError = error;
        this.rows = [];
        this.loadedTeamId = null;
      });
      console.error("[team-sla] failed to load the team's SLA list", { slug, teamId, error });
    } finally {
      runInAction(() => {
        // An older load must not clear the flag out from under a newer one.
        if (isLatest()) this.isLoading = false;
      });
    }
  }

  /** Load the rows only when this team's are not already cached — what the SLA
   *  detail route uses to resolve a deep-linked row's `relation` without
   *  re-fetching on every in-tab navigation. */
  async ensureRows(slug: string, teamId: string): Promise<void> {
    if (this.loadedTeamId === teamId) return;
    await this.loadRows(slug, teamId);
  }

  /** Load the active workflows *teamId*'s family manages, for the create form's
   *  condition selector. Never rejects — see {@link loadRows}. */
  async loadWorkflows(slug: string, teamId: string): Promise<void> {
    if (this.loadedWorkflowsTeamId === teamId) return;
    const token = ++this.workflowsToken;
    const isLatest = () => this.workflowsToken === token;
    try {
      // Never leave the previous team's workflows selectable: picking one would
      // build a payload the server rejects as `sla_unmanaged_workflow`.
      this.workflows = [];
      this.loadedWorkflowsTeamId = null;
      this.isLoadingWorkflows = true;
      const [forms, teams] = await Promise.all([
        this.deps.intakeService.listForms(slug),
        // `includeAll` or the family is computed from the caller's OWN teams: the
        // default list is the teams they have joined plus those teams' descendants,
        // so an admin opening a team they are not a member of found the team absent
        // and was offered no workflows at all (NG-209). The server only honours
        // `all` for a caller holding `workspace.edit`, so a team lead is unaffected
        // — they are a member of the team they lead, and their family already
        // resolves from the default set.
        this.deps.workspaceTeamService.list(slug, { includeAll: true }),
      ]);
      const family = teamFamilyIds(teams, teamId);
      if (family === null) {
        console.warn("[team-sla] team missing from the teams list; offering no workflows", {
          slug,
          teamId,
        });
      }
      runInAction(() => {
        if (!isLatest()) return;
        this.workflows =
          family === null
            ? []
            : forms
                // OWNERSHIP, not audience: "a team manages a workflow" is the
                // workflow's `managing_team` and nothing else (GH-3144). Filtering
                // on `form.teams` — who may SUBMIT through it — would offer a lead
                // every workflow merely aimed at their team, and the server would
                // answer `sla_unmanaged_workflow`. A workspace-managed workflow has
                // no managing team and so belongs in no team's option set.
                .filter(
                  (form) =>
                    form.is_active &&
                    form.managing_team !== null &&
                    family.has(form.managing_team.id),
                )
                .map((form) => ({ id: form.id, name: form.name }));
        this.loadedWorkflowsTeamId = family === null ? null : teamId;
      });
    } catch (err) {
      // Recoverable, but the options must not survive the failure: a stale set
      // is worse than an empty one, because every save built from it 400s.
      runInAction(() => {
        if (!isLatest()) return;
        this.workflows = [];
        this.loadedWorkflowsTeamId = null;
      });
      console.warn("[team-sla] failed to load the team's workflows", { slug, teamId, err });
    } finally {
      runInAction(() => {
        if (isLatest()) this.isLoadingWorkflows = false;
      });
    }
  }

  rowById(slaId: string): ITeamSla | undefined {
    return this.rows.find((row) => row.id === slaId);
  }

  /** Rows bucketed by relation, in the page's render order. */
  get groups(): TTeamSlaGroups {
    const groups: TTeamSlaGroups = { manages: [], inherited: [], residual: [] };
    for (const row of this.rows) {
      const bucket = groups[row.relation];
      if (bucket) bucket.push(row);
    }
    return groups;
  }
}
